import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Banque } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface BanqueState {
  banques: Banque[];
  banque: Banque | null;
  loading: boolean;
  error: string | null;
}

const initialState: BanqueState = {
  banques: [],
  banque: null,
  loading: false,
  error: null,
};


export const fetchBanques = createAsyncThunk<Banque[], void, { state: RootState }>(
  'banque/fetchBanques',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/banques/all/${authentification.user.OwnerId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch banques');
    }
  }
);

export const fetchBanque = createAsyncThunk<Banque, number | void, { state: RootState }>(
  'banque/fetchBanque',
  async (banqueId, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const id = banqueId || 1; // Default à 1
      const response = await axios.get(
        `${baseUrlApi}/banques/${id}`,
        { headers: { Authorization: `bearer ${authentification.user.token}` } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch banque');
    }
  }
);

export const createBanque = createAsyncThunk<Banque, Partial<Banque>, { state: RootState }>(
  'banque/createBanque',
  async (banqueData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/banques/`, banqueData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchBanques());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create banque');
    }
  }
);

export const updateBanque = createAsyncThunk<
  Banque,
  { banqueData: Partial<Banque>; banqueId: number },
  { state: RootState }
>(
  'banque/updateBanque',
  async ({ banqueData, banqueId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(`${baseUrlApi}/banques/${banqueId}`, banqueData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchBanques());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update banque');
    }
  }
);

export const deleteBanque = createAsyncThunk<number, number, { state: RootState }>(
  'banque/deleteBanque',
  async (banqueId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(`${baseUrlApi}/banques/${banqueId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchBanques());
      return banqueId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete banque');
    }
  }
);

const banqueSlice = createSlice({
  name: 'banque',
  initialState,
  reducers: {
    clearBanque: (state) => { state.banque = null; },
    clearBanques: (state) => { state.banques = []; },
    resetBanqueState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchBanques, fetchBanque, createBanque, updateBanque, deleteBanque].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchBanques.fulfilled, (state, action) => {
        state.loading = false;
        state.banques = action.payload;
      })
      .addCase(fetchBanque.fulfilled, (state, action) => {
        state.loading = false;
        state.banque = action.payload;
      })
      .addCase(createBanque.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateBanque.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteBanque.fulfilled, (state, action) => {
        state.loading = false;
        state.banques = state.banques.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearBanque, clearBanques, resetBanqueState } = banqueSlice.actions;
export default banqueSlice.reducer;
